import { useEffect, useLayoutEffect, useState } from "react";
import { Outlet, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { client } from "../services/api/graphql/initialize";
import { getNotifications } from "../request/api/real-api/notification";
import { usePusher } from "../hooks/usePusher";
import { _set_initial_data } from "../store/actions/notification_actions";
import { _Cart_Clean } from "../store/actions/cart_actions";
import {
  _set_channel,
  _set_store,
  _set_pending_channel,
  _set_pending_cid,
  _set_pending_cpid,
  _clear_all_pending,
  set_cid,
  set_cpid,
  set_checkout_id,
  set_transaction_id,
  set_currency,
  set_url_code,
} from "../store/actions/login_actions";

import { BaseChannel, channelDetails } from "../base/env";
import { useIpLanguage } from "../hooks/useIpLanguage";

const MainLayout = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { i18n } = useTranslation();

  const [urlResolved, setUrlResolved] = useState(false);

  const {
    Login: {
      token,
      channel,
      cid: cidState,
      cpid: cpidState,
      channelClaimed,
      pendingChannel,
      pendingCid,
      pendingCpid,
    },
  } = useSelector((state) => state);

  usePusher();

  /**
   * 1️⃣ URL – channel
   */
  const effectiveChannel = pendingChannel || channel || BaseChannel;

  const applyDefaultStoreByChannel = (targetChannel) => {
    if (targetChannel === "canada") {
      dispatch(
        _set_store(
          1150,
          "Canada",
          "canada",
        ),
      );
      return;
    }
      if (targetChannel === "saudi-channel") {
      dispatch(
        _set_store(
          1289,
          "Saudi Arabia",
          "saudi-channel",
        ),
      );
      return;
    }

    dispatch(
      _set_store(
        1,
        "Oman",
        targetChannel || BaseChannel,
      ),
    );
  };

  useLayoutEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlChannel = params.get("channel");
    const flow = params.get("flow");
    const isDirectFlow = flow === "direct";
    const isFlowMissingButAlreadyApplied =
      !flow && urlChannel && urlChannel === channel;

    if (urlChannel) {
      if (isDirectFlow) {
        dispatch(_set_channel(urlChannel));
        // Ensure UI clears immediately when switching channels via direct flow
        dispatch(_Cart_Clean());
        dispatch(set_checkout_id({ checkout_id: "" }));
        dispatch(set_transaction_id(""));
        applyDefaultStoreByChannel(urlChannel);
        dispatch(_clear_all_pending());
        localStorage.setItem("channel", urlChannel);
      } else if (isFlowMissingButAlreadyApplied) {
        // Prevent re-populating pendingChannel after a direct flow already applied this channel.
        dispatch(_set_pending_channel(""));
      } else {
        dispatch(_set_pending_channel(urlChannel));
      }
      params.delete("channel");
      setSearchParams(params, { replace: true });
    }

    setUrlResolved(true);
  }, [location.search, channel]);

  /**
   * 2️⃣ IP
   */
  useIpLanguage({ urlResolved });

  /**
   * Notifications
   */
  useEffect(() => {
    const interest = [];
    let currentInterest = "public-gmall";
    interest.push(currentInterest);

    if (cidState) {
      currentInterest = `${currentInterest}-${cidState}`;
      interest.push(currentInterest);
    }

    if (cpidState) {
      currentInterest = `${currentInterest}-${cpidState}`;
      interest.push(currentInterest);
    }

    currentInterest = `${currentInterest}-${effectiveChannel}`;
    interest.push(currentInterest);

    getNotifications({ interests: interest }).then((res) => {
      if (res.code === 200) {
        dispatch(_set_initial_data(res.data));
      }
    });
  }, [token, i18n.language, cidState, cpidState, effectiveChannel, dispatch]);

  /**
   * Currency based on channel
   */
  useEffect(() => {
    if (channel && channelDetails[channel]) {
      dispatch(set_currency(channelDetails[channel].currency));
    }
  }, [channel, dispatch]);

  /**
   * i18n direction + refetch queries
   */
  useEffect(() => {
    document.body.dir = i18n.dir();
    client.refetchQueries({ include: "all" });
  }, [i18n.language, i18n]);

  /**
   * Scroll to top on route change
   */
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  /**
   * URL params: cid
   */
  useEffect(() => {
    const cid = searchParams.get("cid");
    const flow = searchParams.get("flow");
    const isDirectFlow = flow === "direct";
    if (cid) {
      if (isDirectFlow) {
        dispatch(set_cid(cid));
        dispatch(_clear_all_pending());
        localStorage.setItem("cid", cid);
      } else {
        dispatch(_set_pending_cid(cid));
      }
      searchParams.delete("cid");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, location.pathname]);

  /**
   * URL params: cpid
   */
  useEffect(() => {
    const cpid = searchParams.get("cpid");
    const flow = searchParams.get("flow");
    const isDirectFlow = flow === "direct";
    if (cpid) {
      if (isDirectFlow) {
        dispatch(set_cpid(cpid));
        dispatch(_clear_all_pending());
        localStorage.setItem("cpid", cpid);
      } else {
        dispatch(_set_pending_cpid(cpid));
      }
      searchParams.delete("cpid");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams]);

  useEffect(() => {
    const flow = searchParams.get("flow");
    if (flow) {
      searchParams.delete("flow");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams]);

  /**
   * URL params: urlCode
   */
  useEffect(() => {
    const urlCode = searchParams.get("urlCode");
    if (urlCode) {
      dispatch(set_url_code(urlCode));
      searchParams.delete("urlCode");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, dispatch, setSearchParams]);

  /**
   * Legacy cid from localStorage
   */
  useEffect(() => {
    const oldCid = localStorage.getItem("cid");
    if (!oldCid) return;
    if (pendingCid) {
      localStorage.removeItem("cid");
      return;
    }
    if (oldCid && !cidState) {
      dispatch(set_cid(oldCid));
    }
    localStorage.removeItem("cid");
  }, [cidState, pendingCid, dispatch]);

  /**
   * Persist committed promo identifiers (claim flow).
   * Direct flow already sets these explicitly, but claim flow only commits to redux.
   */
  useEffect(() => {
    // Only persist after claim is committed (no pending values)
    if (!channelClaimed) return;
    if (pendingChannel || pendingCid || pendingCpid) return;

    if (channel) localStorage.setItem("channel", channel);
    if (cidState) localStorage.setItem("cid", cidState);
    if (cpidState) localStorage.setItem("cpid", cpidState);
  }, [
    channel,
    cidState,
    cpidState,
    channelClaimed,
    pendingChannel,
    pendingCid,
    pendingCpid,
  ]);

  /**
   * default cpid
   */
  useLayoutEffect(() => {
    if (cpidState === undefined) {
      dispatch(set_cpid("gmall"));
    }
  }, [cpidState, dispatch]);

  return (
    <>
      <Outlet />
    </>
  );
};

export default MainLayout;
